import React, { useEffect, useState } from 'react'
import { Box, Collapse, IconButton, TextField, Typography } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import firebase from 'infrastructure/firebase'
import { GoogleLoginButton } from 'infrastructure/googleDrive'
import { useGoogleAuth } from './hooks'
import { PrepareStage } from './PrepareStage'
import { ProcessStage } from './ProcessStage'
import { ResultsDisplay } from './ResultsDisplay'
import {
  googleAuthOverrideAtom,
  prepareResultAtom,
  processResultAtom,
  selectedLessonIdsAtom,
} from './atoms'
import type { SlidesGeneratorProps } from './types'

function LoggedInUser({ user }: { user: firebase.User }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Signed in as {user.email}
    </Typography>
  )
}

export function SlidesGenerator({ treeId, versionId }: SlidesGeneratorProps) {
  const { isAuthenticated } = useGoogleAuth()
  const setPrepareResult = useSetAtom(prepareResultAtom)
  const setProcessResult = useSetAtom(processResultAtom)
  const setSelectedLessonIds = useSetAtom(selectedLessonIdsAtom)
  const googleAuthOverride = useAtomValue(googleAuthOverrideAtom)
  const setGoogleAuthOverride = useSetAtom(googleAuthOverrideAtom)
  const [debugOpen, setDebugOpen] = useState(false)

  useEffect(() => {
    setPrepareResult(null)
    setProcessResult(null)
    setSelectedLessonIds(new Set())
  }, [
    treeId,
    versionId,
    setPrepareResult,
    setProcessResult,
    setSelectedLessonIds,
  ])

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          onClick={() => setDebugOpen((o) => !o)}
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          <IconButton
            size="small"
            aria-label={debugOpen ? 'Collapse' : 'Expand'}
          >
            {debugOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            Google auth override (debug)
          </Typography>
        </Box>
        <Collapse in={debugOpen}>
          <TextField
            fullWidth
            size="small"
            label="Google auth override (debug)"
            placeholder="Access token or auth value"
            value={googleAuthOverride ?? ''}
            onChange={(e) =>
              setGoogleAuthOverride(e.target.value.trim() || null)
            }
            sx={{ mt: 0.5, maxWidth: 600 }}
          />
        </Collapse>
      </Box>

      <Typography variant="h5" gutterBottom>
        Slides Generator
      </Typography>
      <GoogleLoginButton
        LoggedInComponent={LoggedInUser}
        scopes={[
          'https://www.googleapis.com/auth/drive',
          'https://www.googleapis.com/auth/drive.file',
          'https://www.googleapis.com/auth/presentations',
        ]}
      />
      {isAuthenticated && (
        <>
          <PrepareStage treeId={treeId} versionId={versionId} />
          <ProcessStage treeId={treeId} versionId={versionId} />
          <ResultsDisplay treeId={treeId} versionId={versionId} />
        </>
      )}
    </Box>
  )
}
